import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangleIcon, CheckCircle2Icon, MailIcon } from 'lucide-react';
import { AuthSplit } from '../../components/auth/AuthSplit';
import { PasswordField } from '../../components/auth/PasswordField';
import { Button } from '../../components/ui/Button';
import { Field, Input } from '../../components/ui/Field';
import { useAuth } from '../../contexts/AuthContext';
type Stage = 'identify' | 'code' | 'done';
function AuthHeading({
  title,
  intro
}: {
  title: string;
  intro: string;
}) {
  return <>
      <h1 className="text-[clamp(1.6rem,3vw,2.1rem)] font-semibold leading-tight text-ink" data-id="element-8171" data-mp="n3veansvkzmx">
        {title}
      </h1>
      <p className="mb-6 mt-3 text-body text-ink-muted" data-id="element-8172" data-mp="s2ezglzna7e8">{intro}</p>
    </>;
}
export function ForgotPassword() {
  const {
    requestReset,
    confirmReset,
    passwordVerdict
  } = useAuth();
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>('identify');
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  /* Preview-only surfacing of the emailed code. */
  const [previewCode, setPreviewCode] = useState<string | null>(null);
  const verdict = passwordVerdict(password, [identifier]);
  const mismatch = confirm.length > 0 && confirm !== password;
  function onIdentify(event: React.FormEvent) {
    event.preventDefault();
    const outcome = requestReset(identifier);
    if (!outcome.ok) {
      setError(outcome.error);
      return;
    }
    setPreviewCode(outcome.code);
    setError(null);
    setStage('code');
  }
  function onReset(event: React.FormEvent) {
    event.preventDefault();
    if (mismatch || !confirm) {
      setError('Both passwords must match.');
      return;
    }
    const outcome = confirmReset(code, password);
    if (!outcome.ok) {
      setError(outcome.error);
      return;
    }
    setError(null);
    setStage('done');
  }
  if (stage === 'done') {
    return <AuthSplit headline={['Back in.', 'Your campus.', 'One account.']} lead="Changing your password ends every other session, so anyone signed in elsewhere is now out." data-id="element-8173" data-mp="3xifb6whyeln">
        <AuthHeading title="Your password is changed" intro="Every other session has been signed out, so anyone using your account elsewhere has lost access." data-id="element-8174" data-mp="cz88oi9xvnm2" />
        <p className="flex items-start gap-2 text-body text-ink" data-id="element-8175" data-mp="3hcoju9tfr36v">
          <CheckCircle2Icon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-ok-700" data-id="element-8176" data-mp="3hatxcr2in30o" />
          Sign in with your new password. The recovery code you used cannot be used again.
        </p>
        <div className="mt-5" data-id="element-8177" data-mp="3q6jfmr8o3h9u">
          <Button variant="primary" block onClick={() => navigate('/signin', {
          replace: true
        })} data-id="element-8178" data-mp="2tnc0wi8b3grd">
            Log in
          </Button>
        </div>
      </AuthSplit>;
  }
  if (stage === 'code') {
    return <AuthSplit headline={['Set it again.', 'Your campus.', 'One account.']} lead="One password, one identity — changing it here changes it everywhere you use Campuzon." badge="Recovery codes expire in 10 minutes" data-id="element-8179" data-mp="2q5y26k3mf7sr">
        <AuthHeading title="Choose a new password" intro="Enter the code we emailed, then set a new password. The code expires in 10 minutes and works once." data-id="element-8180" data-mp="2h2wyeq289ss9" />

        <form onSubmit={onReset} noValidate className="space-y-5" data-id="element-8181" data-mp="27850fl4qh3wy">
          <Field label="Recovery code" htmlFor="reset-code" hint="Six digits." data-id="element-8182" data-mp="22flm2ly824r3">
            <Input id="reset-code" value={code} onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" className="cz-num tracking-[0.3em]" data-id="element-8183" data-mp="1xghd9wf7m91a" />
          </Field>

          <PasswordField id="reset-password" label="New password" value={password} onChange={setPassword} autoComplete="new-password" verdict={verdict} data-id="element-8184" data-mp="1ji0yrjb3ve8l" />

          <PasswordField id="reset-confirm" label="Confirm new password" value={confirm} onChange={setConfirm} autoComplete="new-password" error={mismatch ? 'These two passwords do not match.' : null} data-id="element-8185" data-mp="3okeouvv1e9h8" />

          {error && <p role="alert" className="flex items-start gap-2 rounded-md border border-danger-100 bg-danger-50 px-3 py-2.5 text-body-sm text-danger-700" data-id="element-8186" data-mp="30v3vw0jxhjdn">
              <AlertTriangleIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-8187" data-mp="2qwwifrouljge" />
              {error}
            </p>}

          <Button type="submit" variant="primary" block disabled={code.length !== 6 || !password || !confirm} data-id="element-8188" data-mp="p2owll32s8vo">
            Change password
          </Button>
        </form>

        {previewCode && <div className="mt-6 rounded-card border border-line bg-canvas p-4" data-id="element-8189" data-mp="2ei7ypghjm6zo">
            <p className="flex items-start gap-2 text-body-sm text-ink" data-id="element-8190" data-mp="29msmsu33il5w">
              <MailIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" data-id="element-8191" data-mp="2ob6y7tekvhy2" />
              <span data-id="element-8192" data-mp="2jj5drk79akwy">
                Preview build: your recovery code is{' '}
                <span className="cz-num font-semibold tracking-widest" data-id="element-8193" data-mp="2xajwrr15ym40">{previewCode}</span>. In
                production this only reaches your inbox.
              </span>
            </p>
          </div>}
      </AuthSplit>;
  }
  return <AuthSplit headline={['Locked out?', 'Your campus', 'is still yours.']} lead="Recovery restores access to the one account you already have — it never creates a second one." aside={<span className="text-ink-muted" data-id="element-8195" data-mp="1cvi84t6122t1">
          Remembered it?{' '}
          <Link to="/signin" className="font-semibold text-brand-700 underline-offset-2 hover:underline" data-id="element-8196" data-mp="17wxan1i5tx8a">
            Log in instead
          </Link>
        </span>} data-id="element-8194" data-mp="378hk8cb78evk">
      <AuthHeading title="Recover your account" intro="Tell us your username or email address and we will send a recovery code." data-id="element-8197" data-mp="21me7lh90qsj6" />

      <form onSubmit={onIdentify} noValidate className="space-y-5" data-id="element-8198" data-mp="2bjsiz2g34r4m">
        <Field label="Username or email" htmlFor="recover-identifier" data-id="element-8199" data-mp="2bl3n53dg00w9">
          <Input id="recover-identifier" value={identifier} onChange={event => setIdentifier(event.target.value)} autoComplete="username" spellCheck={false} data-id="element-8200" data-mp="2gi3vjkxrlfqj" />
        </Field>

        {error && <p role="alert" className="flex items-start gap-2 rounded-md border border-danger-100 bg-danger-50 px-3 py-2.5 text-body-sm text-danger-700" data-id="element-8201" data-mp="2uatjvw0rmwqt">
            <AlertTriangleIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-8202" data-mp="3487zxuis1jil" />
            {error}
          </p>}

        <Button type="submit" variant="primary" block disabled={!identifier.trim()} data-id="element-8203" data-mp="sle642zmipuw">
          Send recovery code
        </Button>

        <p className="border-t border-line pt-4 text-caption text-ink-muted" data-id="element-8204" data-mp="nkgivbzrb5mj">
          We always report success here, whether or not those details match an account. Confirming
          which addresses are registered would let a stranger discover who has a Campuzon account.
        </p>
      </form>
    </AuthSplit>;
}